import React, { useRef } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { Card, Field, ColorInput, CssColorField, TextField, SaveButton, useSection } from '../AdminUI';

function fileToDataUrl(file, maxSize = 600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const scale = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png', 0.92));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function BrandingSection() {
  const [local, setLocal, save] = useSection('branding');
  const inputRef = useRef(null);
  const favRef = useRef(null);
  const set = (k, v) => setLocal({ ...local, [k]: v });

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 400);
    set('logoImage', dataUrl);
  };
  const handleFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 128); // small square
    set('favicon', dataUrl);
  };

  const favShape = local.faviconShape || 'square';
  const shapeClass = favShape === 'circle' ? 'rounded-full' : favShape === 'rounded' ? 'rounded-lg' : 'rounded-sm';

  return (
    <Card title="Branding & Theme" desc="Logo, favicon, tagline, theme colors and header style." footer={<SaveButton onClick={save} />}>
      <div className="mb-5">
        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Logo Image (optional - overrides text logo)</label>
        <div className="flex items-center gap-3">
          <div className="h-16 w-32 rounded-md border border-zinc-800 bg-zinc-900 flex items-center justify-center overflow-hidden">
            {local.logoImage ? (
              <img src={local.logoImage} alt="logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-zinc-600 text-xs">No image</span>
            )}
          </div>
          <input ref={inputRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" />
          <button onClick={() => inputRef.current?.click()} className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm flex items-center gap-1.5" data-testid="branding-logo-upload-btn">
            <Upload size={14} /> Upload Logo
          </button>
          {local.logoImage && (
            <button onClick={() => set('logoImage', '')} className="h-9 px-3 rounded-md bg-red-600/15 hover:bg-red-600/25 border border-red-900/40 text-red-300 text-sm flex items-center gap-1.5">
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">PNG / JPG / SVG recommended. Max width 400px (auto-resized).</p>
      </div>

      {/* Logo shape selector */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Logo Shape (header display)</label>
        <div className="flex gap-2 flex-wrap" data-testid="logo-shape-picker">
          {[
            { k: 'auto', label: 'Auto (free shape)', preview: 'rounded-none' },
            { k: 'square', label: 'Square', preview: '' },
            { k: 'rounded', label: 'Rounded', preview: 'rounded-lg' },
            { k: 'circle', label: 'Circle', preview: 'rounded-full' },
          ].map((opt) => {
            const on = (local.logoShape || 'auto') === opt.k;
            return (
              <button
                key={opt.k}
                onClick={() => set('logoShape', opt.k)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-semibold ${on ? 'border-[color:var(--gold)] bg-[color:var(--gold)]/15 text-[color:var(--gold)]' : 'border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700'}`}
                data-testid={`logo-shape-${opt.k}`}
              >
                <span className={`w-6 h-6 bg-zinc-700 ${opt.preview}`} />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Logo Size - dynamic +/- buttons */}
      <div className="mb-5 pt-4 border-t border-zinc-800">
        <label className="text-xs font-semibold text-zinc-300 mb-2 block">Logo Size (Height)</label>
        <div className="flex items-center gap-3">
          <button
            onClick={() => set('logoSize', Math.max(24, (local.logoSize || 40) - 4))}
            className="w-9 h-9 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-xl font-bold flex items-center justify-center active:scale-95 transition select-none"
            aria-label="Decrease logo size"
          >−</button>
          <input
            type="range"
            min={24} max={120} step={4}
            value={local.logoSize || 40}
            onChange={e => set('logoSize', Number(e.target.value))}
            className="flex-1 accent-[#ccc77d]"
          />
          <button
            onClick={() => set('logoSize', Math.min(120, (local.logoSize || 40) + 4))}
            className="w-9 h-9 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-xl font-bold flex items-center justify-center active:scale-95 transition select-none"
            aria-label="Increase logo size"
          >+</button>
          <span className="text-xs text-[#ccc77d] font-bold w-10 text-right shrink-0">{local.logoSize || 40}px</span>
        </div>
        <div className="mt-3 flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3">
          <span className="text-xs text-zinc-500 shrink-0">Preview:</span>
          {local.logoImage ? (
            <img src={local.logoImage} alt="logo preview" style={{ height: `${local.logoSize || 40}px` }} className="object-contain max-w-[200px]" />
          ) : (
            <span style={{ fontSize: `${Math.round((local.logoSize || 40) * 0.55)}px` }} className="font-black text-white tracking-tight leading-none">
              {local.logoText || 'REDDY'}<span className="opacity-80 ml-1">{local.logoAccent || 'ANNA'}</span>
            </span>
          )}
        </div>
      </div>

      {/* Favicon */}
      <div className="mb-5 pt-5 border-t border-zinc-800">
        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Favicon (browser tab icon)</label>
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`w-14 h-14 border border-zinc-800 bg-zinc-900 flex items-center justify-center overflow-hidden ${shapeClass}`} data-testid="branding-favicon-preview">
            {local.favicon ? (
              <img src={local.favicon} alt="favicon" className="w-full h-full object-cover" />
            ) : (
              <span className="text-zinc-600 text-[9px]">none</span>
            )}
          </div>
          <input ref={favRef} type="file" accept="image/*" onChange={handleFavicon} className="hidden" />
          <button onClick={() => favRef.current?.click()} className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm flex items-center gap-1.5" data-testid="branding-favicon-upload-btn">
            <Upload size={14} /> Upload Favicon
          </button>
          {local.favicon && (
            <button onClick={() => set('favicon', '')} className="h-9 px-3 rounded-md bg-red-600/15 hover:bg-red-600/25 border border-red-900/40 text-red-300 text-sm flex items-center gap-1.5">
              <Trash2 size={14} /> Remove
            </button>
          )}
          <div className="ml-auto flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-md p-1">
            {[
              { v: 'square', label: 'Square' },
              { v: 'rounded', label: 'Rounded' },
              { v: 'circle', label: 'Circle' }
            ].map((s) => (
              <button key={s.v} onClick={() => set('faviconShape', s.v)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-sm transition ${favShape === s.v ? 'bg-[color:var(--gold)] text-black' : 'text-zinc-300 hover:text-white'}`}
                data-testid={`branding-favicon-shape-${s.v}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">Square 64×64 / 128×128 image recommended. The shape is applied via CSS clipping.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Logo Main Text (fallback if no image)"><TextField value={local.logoText} onChange={(v) => set('logoText', v)} /></Field>
        <Field label="Logo Accent Text"><TextField value={local.logoAccent} onChange={(v) => set('logoAccent', v)} /></Field>
        <Field label="Tagline"><TextField value={local.tagline} onChange={(v) => set('tagline', v)} /></Field>
        <Field label="Accent Gold"><ColorInput value={local.accent} onChange={(v) => set('accent', v)} /></Field>
        <Field label="Accent Dark"><ColorInput value={local.accentDark} onChange={(v) => set('accentDark', v)} /></Field>
        <Field label="Active Green"><ColorInput value={local.activeGreen} onChange={(v) => set('activeGreen', v)} /></Field>
        <Field label="Page Background"><ColorInput value={local.pageBg} onChange={(v) => set('pageBg', v)} /></Field>
        <Field label="Header Background (CSS)" hint="Any valid CSS background — pick a solid colour with the picker, or paste a gradient.">
          <CssColorField value={local.headerBg} onChange={(v) => set('headerBg', v)} placeholder="linear-gradient(...)" />
        </Field>
      </div>
      <div className="mt-4">
        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Header Preview</label>
        <div className="h-14 rounded-md border border-zinc-800" style={{ background: local.headerBg }} />
      </div>
    </Card>
  );
}
